import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2, Plus, Building2 } from "lucide-react";

export default function WorksitesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/worksites");
      setItems(data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (ws) => {
    try {
      if (ws.active) await api.delete(`/tenant/worksites/${ws.id}`);
      else await api.patch(`/tenant/worksites/${ws.id}`, { active: true });
      toast.success(ws.active ? "Worksite disabled" : "Worksite re-activated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="worksites-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Administration</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Worksites</h1>
        <p className="mt-2 text-muted-foreground">Locations where crews clock in. Soft delete is safe — historical entries remain readable.</p>
      </div>

      <div><NewWorksite onDone={load} /></div>

      <Card>
        <CardHeader><CardTitle>All worksites</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>}
              {!loading && items.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No worksites yet — create your first one.</TableCell></TableRow>}
              {items.map((w) => (
                <TableRow key={w.id} data-testid={`worksite-row-${w.code}`}>
                  <TableCell className="font-medium">{w.name}</TableCell>
                  <TableCell><Badge variant="secondary" className="rounded-full font-mono text-xs">{w.code}</Badge></TableCell>
                  <TableCell className="max-w-xs truncate text-muted-foreground">{w.address || "—"}</TableCell>
                  <TableCell>{w.assigned_count}</TableCell>
                  <TableCell>
                    <Badge variant={w.active ? "default" : "outline"} className="capitalize">{w.active ? "active" : "inactive"}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button asChild size="sm" variant="ghost"><Link to={`/app/admin/worksites/${w.id}`}>Assignments</Link></Button>
                      <Button size="sm" variant="outline" onClick={() => toggleActive(w)} data-testid={`toggle-worksite-${w.code}`}>
                        {w.active ? "Disable" : "Re-activate"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function NewWorksite({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", code: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/tenant/worksites", f);
      toast.success("Worksite created");
      setOpen(false); setF({ name: "", code: "", address: "" });
      onDone();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-worksite-btn"><Plus className="h-4 w-4" /> New worksite</Button>
      </DialogTrigger>
      <DialogContent data-testid="new-worksite-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" /> New worksite</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Name</Label><Input value={f.name} onChange={set("name")} placeholder="North Bay Yard" data-testid="ws-name" /></div>
          <div className="space-y-2"><Label>Code</Label><Input value={f.code} onChange={set("code")} placeholder="NB-01" data-testid="ws-code" /></div>
          <div className="space-y-2"><Label>Address (optional)</Label><Input value={f.address} onChange={set("address")} data-testid="ws-address" /></div>
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !f.name || !f.code} data-testid="ws-save-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
